import React, { useState, useEffect, useRef, useMemo } from "react";
import { Play, Pause, RotateCcw, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

interface ArticleAudioPlayerProps {
  title: string;
  content: string;
  readTimeMinutes?: number;
  className?: string;
}

export function ArticleAudioPlayer({
  title,
  content,
  readTimeMinutes = 4,
  className,
}: ArticleAudioPlayerProps) {
  const [supported] = useState(() => typeof window !== "undefined" && "speechSynthesis" in window);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [rate, setRate] = useState<number>(1.0);
  const [progress, setProgress] = useState(0);

  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const textChunksRef = useRef<string[]>([]);
  const currentChunkIndexRef = useRef<number>(0);

  // Clean raw HTML / markdown content to spoken plain text
  const cleanText = useMemo(() => {
    if (typeof window === "undefined") return "";
    const tmp = document.createElement("div");
    tmp.innerHTML = content;
    const stripped = tmp.textContent || tmp.innerText || "";
    return `${title}. ${stripped.replace(/\s+/g, " ").trim()}`;
  }, [title, content]);

  // Split text into safe chunks (~200 chars at sentence boundaries) to prevent browser speech clipping
  useEffect(() => {
    if (!cleanText) return;
    const sentences = cleanText.match(/[^.!?]+[.!?]+/g) || [cleanText];
    const chunks: string[] = [];
    let current = "";

    for (const s of sentences) {
      if ((current + s).length > 200) {
        if (current) chunks.push(current.trim());
        current = s;
      } else {
        current += s;
      }
    }
    if (current) chunks.push(current.trim());
    textChunksRef.current = chunks;
  }, [cleanText]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (typeof window !== "undefined" && "speechSynthesis" in window) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  const speakCurrentChunk = () => {
    if (typeof window === "undefined" || !("speechSynthesis" in window)) return;
    const chunks = textChunksRef.current;
    const idx = currentChunkIndexRef.current;

    if (idx >= chunks.length) {
      setIsPlaying(false);
      setIsPaused(false);
      setProgress(100);
      currentChunkIndexRef.current = 0;
      return;
    }

    window.speechSynthesis.cancel();

    const utter = new SpeechSynthesisUtterance(chunks[idx]);
    utter.rate = rate;
    utter.pitch = 1.0;

    utter.onend = () => {
      currentChunkIndexRef.current += 1;
      const pct = Math.round((currentChunkIndexRef.current / chunks.length) * 100);
      setProgress(pct);
      if (currentChunkIndexRef.current < chunks.length) {
        speakCurrentChunk();
      } else {
        setIsPlaying(false);
        setIsPaused(false);
        currentChunkIndexRef.current = 0;
      }
    };

    utter.onerror = () => {
      setIsPlaying(false);
      setIsPaused(false);
    };

    utteranceRef.current = utter;
    window.speechSynthesis.speak(utter);
  };

  const handlePlayPause = () => {
    if (typeof window === "undefined" || !("speechSynthesis" in window)) return;

    if (isPlaying && !isPaused) {
      window.speechSynthesis.pause();
      setIsPaused(true);
    } else if (isPaused) {
      window.speechSynthesis.resume();
      setIsPaused(false);
    } else {
      setIsPlaying(true);
      setIsPaused(false);
      speakCurrentChunk();
    }
  };

  const handleReset = () => {
    if (typeof window === "undefined" || !("speechSynthesis" in window)) return;
    window.speechSynthesis.cancel();
    currentChunkIndexRef.current = 0;
    setProgress(0);
    setIsPlaying(false);
    setIsPaused(false);
  };

  const cycleSpeed = () => {
    const speeds = [1.0, 1.25, 1.5, 1.75];
    const nextIdx = (speeds.indexOf(rate) + 1) % speeds.length;
    const nextRate = speeds[nextIdx];
    setRate(nextRate);
    if (isPlaying && !isPaused) {
      speakCurrentChunk();
    }
  };

  if (!supported) return null;

  return (
    <div
      className={cn(
        "rounded-2xl border border-border/60 bg-card/60 backdrop-blur-md p-4 sm:p-5 shadow-sm transition-all duration-300",
        isPlaying && !isPaused && "border-primary/40 bg-primary/5 shadow-md shadow-primary/5",
        className,
      )}
    >
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        {/* Left: Indicator, Title & Waveform */}
        <div className="flex items-center gap-3.5 min-w-0">
          <button
            onClick={handlePlayPause}
            className={cn(
              "h-11 w-11 min-h-[44px] min-w-[44px] shrink-0 rounded-full flex items-center justify-center transition-all duration-200 shadow-sm active:scale-95",
              isPlaying && !isPaused
                ? "bg-primary text-primary-foreground shadow-primary/25"
                : "bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground",
            )}
            aria-label={isPlaying && !isPaused ? "Pause audio narration" : "Listen to article audio"}
            title={isPlaying && !isPaused ? "Pause narration" : "Listen to article"}
          >
            {isPlaying && !isPaused ? (
              <Pause className="h-5 w-5 fill-current" />
            ) : (
              <Play className="h-5 w-5 fill-current ml-0.5" />
            )}
          </button>

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold uppercase tracking-wider text-primary flex items-center gap-1">
                <Sparkles className="h-3 w-3" /> Audio Narration
              </span>
              <span className="text-xs text-muted-foreground">·</span>
              <span className="text-xs text-muted-foreground font-medium">
                {readTimeMinutes} min listen
              </span>
            </div>
            <p className="text-sm font-semibold text-foreground truncate mt-0.5">
              {isPlaying ? (isPaused ? "Narration paused" : "Listening to story…") : "Listen to this article"}
            </p>
          </div>
        </div>

        {/* Right: Waveform & Speed Controls */}
        <div className="flex items-center justify-between sm:justify-end gap-3 shrink-0">
          {/* Animated 5-bar Waveform Visualizer */}
          <div className="flex items-center gap-1 h-5 px-2 py-1 bg-background/60 rounded-full border border-border/40" aria-hidden="true">
            {[40, 80, 55, 95, 60].map((h, i) => (
              <motion.span
                key={i}
                animate={
                  isPlaying && !isPaused
                    ? {
                        height: ["20%", `${h}%`, "30%"],
                      }
                    : { height: "25%" }
                }
                transition={
                  isPlaying && !isPaused
                    ? {
                        duration: 0.6 + i * 0.15,
                        repeat: Infinity,
                        repeatType: "reverse",
                        ease: "easeInOut",
                      }
                    : { duration: 0.2 }
                }
                className="w-1 bg-primary rounded-full transition-all"
                style={{ height: `${h}%` }}
              />
            ))}
          </div>

          {/* Speed Toggle Pill */}
          <button
            onClick={cycleSpeed}
            className="h-8 px-2.5 rounded-full border border-border/60 bg-background/80 hover:bg-accent text-xs font-semibold text-foreground transition-colors"
            aria-label={`Playback speed ${rate}x, click to change`}
            title="Change narration speed"
          >
            {rate}×
          </button>

          {/* Reset / Stop Button */}
          {isPlaying && (
            <button
              onClick={handleReset}
              className="h-8 w-8 rounded-full border border-border/60 bg-background/80 hover:bg-destructive/10 hover:text-destructive text-muted-foreground flex items-center justify-center transition-colors"
              aria-label="Restart audio from beginning"
              title="Restart"
            >
              <RotateCcw className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Audio Progress Bar */}
      {isPlaying && (
        <div className="mt-3.5 h-1.5 w-full bg-muted/60 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-primary via-[#8A2EE4] to-[#F72074] rounded-full"
            style={{ width: `${progress}%` }}
            transition={{ ease: "easeOut", duration: 0.3 }}
          />
        </div>
      )}
    </div>
  );
}
